/** Replays explicit prompts once embedded Chat is ready; discards queued intent across routes or identities. */
import { createContext, useCallback, useContext, useEffect, useRef, ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useTeams } from './TeamContext';
import { useEventBus } from './EventBusContext';
import { EVENTS, type ChatOpenEventData } from '../constants/eventConstants';
import { chatService } from '../services/chatService';

function useChatPanelState() {
  const { currentUserId, currentAccessLevel, teams } = useTeams();
  const { pathname } = useLocation();
  const { subscribe, unsubscribe, publish } = useEventBus();
  const chatReady = useRef(false);
  const pendingChat = useRef<{ identityKey: string; pathname: string; payload: string | ChatOpenEventData } | null>(null);
  const identityKey = JSON.stringify([currentUserId, currentAccessLevel, teams.map(team => [team.teamId, team.teamName]).sort()]);
  const renderedIdentity = useRef(identityKey);
  const planIdentity = useRef(identityKey);
  const renderedPath = useRef(pathname);
  renderedIdentity.current = identityKey;
  renderedPath.current = pathname;
  useEffect(() => {
    if (planIdentity.current !== identityKey) chatService.clearPlanProgress();
    planIdentity.current = identityKey;
  }, [identityKey]);

  const setChatReady = useCallback((ready: boolean) => {
    chatReady.current = ready;
    if (ready && pendingChat.current !== null) {
      const queued = pendingChat.current;
      pendingChat.current = null;
      if (queued.identityKey === renderedIdentity.current && queued.pathname === renderedPath.current) publish(EVENTS.CHAT_OPEN, queued.payload);
    }
  }, [publish]);

  useEffect(() => {
    const changed = () => { pendingChat.current = null; };
    window.addEventListener('AUTH_REFRESH', changed);
    return () => window.removeEventListener('AUTH_REFRESH', changed);
  }, []);
  useEffect(() => {
    const explicit = (data: string | ChatOpenEventData) => {
      if (pathname.startsWith('/chat')) return;
      if (!chatReady.current) pendingChat.current = { identityKey, pathname, payload: data };
    };
    subscribe(EVENTS.CHAT_OPEN, explicit);
    return () => {
      unsubscribe(EVENTS.CHAT_OPEN, explicit);
      pendingChat.current = null;
    };
  }, [pathname, identityKey, subscribe, unsubscribe]);

  return { setChatReady };
}

const ChatPanelContext = createContext<ReturnType<typeof useChatPanelState> | null>(null);
export function ChatPanelProvider({ children }: { children: ReactNode }) {
  const value = useChatPanelState();
  return <ChatPanelContext.Provider value={value}>{children}</ChatPanelContext.Provider>;
}
export const useChatPanel = () => useContext(ChatPanelContext);
